import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { FileText, Upload, Trash2, Download, Loader2, FileType2 } from "lucide-react";
import { toast } from "sonner";

const fmtBytes = (b) => {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

export default function InvoicesPanel({ job, onChanged }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const invoices = job.invoices || [];

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const form = new FormData();
        form.append("file", file);
        await api.post(`/jobs/${job.id}/invoices`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      const { data } = await api.get(`/jobs/${job.id}`);
      onChanged?.(data);
      toast.success(`${files.length} fattura/e caricata/e`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = async (inv) => {
    try {
      const res = await api.get(`/jobs/${job.id}/invoices/${inv.id}/download`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: inv.content_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = inv.original_filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_e) {
      toast.error("Download non riuscito");
    }
  };

  const remove = async (invId) => {
    if (!confirm("Eliminare questa fattura?")) return;
    try {
      await api.delete(`/jobs/${job.id}/invoices/${invId}`);
      const { data } = await api.get(`/jobs/${job.id}`);
      onChanged?.(data);
      toast.success("Fattura eliminata");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="invoices-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-bold text-slate-900 flex items-center gap-2">
          <FileText className="w-4 h-4 text-sky-600" /> Fatture
          <span className="text-xs font-mono text-slate-400">({invoices.length})</span>
        </h3>
        {isAdmin && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/webp"
              multiple
              onChange={(e) => upload(Array.from(e.target.files || []))}
              className="hidden"
              data-testid="invoice-upload-input"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold px-3 py-2 rounded-lg disabled:opacity-60"
              data-testid="invoice-upload-button"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {uploading ? "Caricamento…" : "Carica Fattura"}
            </button>
          </>
        )}
      </div>

      {invoices.length === 0 ? (
        <div className="text-sm text-slate-400 italic text-center py-6 border border-dashed border-slate-200 rounded-xl">
          Nessuna fattura allegata.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100 border border-slate-100 rounded-xl">
          {invoices.map((inv) => (
            <li
              key={inv.id}
              className="px-3 py-3 flex items-center gap-3"
              data-testid={`invoice-${inv.id}`}
            >
              <div className="w-10 h-10 rounded-xl bg-sky-50 text-sky-700 flex items-center justify-center shrink-0 border border-sky-100">
                <FileType2 className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 text-sm truncate">
                  {inv.original_filename}
                </div>
                <div className="text-[11px] text-slate-500 font-mono">
                  {inv.uploaded_by_name} · {inv.uploaded_at?.slice(0, 10)} · {fmtBytes(inv.size)}
                </div>
              </div>
              <button
                onClick={() => download(inv)}
                className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
                title="Scarica"
                data-testid={`download-invoice-${inv.id}`}
              >
                <Download className="w-4 h-4" />
              </button>
              {isAdmin && (
                <button
                  onClick={() => remove(inv.id)}
                  className="p-2 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600"
                  data-testid={`delete-invoice-${inv.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="text-[11px] text-slate-400 mt-3">
        Formati supportati: PDF, JPG, PNG, WEBP · max 20 MB. Le fatture sono visibili solo agli amministratori.
      </div>
    </section>
  );
}
